import { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { PageHeader, Loading, EmptyState, inr, DemoBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { toast } from "sonner";
import { Users, Plus, Upload, Trash2, Pencil, ScrollText } from "lucide-react";

export default function Members() {
  const [members, setMembers] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [ledger, setLedger] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/members").then((r) => setMembers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.flat) { toast.error("Name and flat are required"); return; }
    try {
      if (edit) await api.put(`/members/${edit}`, form);
      else await api.post("/members", form);
      toast.success(edit ? "Member updated" : "Member added");
      setOpen(false); setForm({}); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this member?")) return;
    await api.delete(`/members/${id}`); toast.success("Deleted"); load();
  };

  const doImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/members/import", fd);
      toast.success(`Imported ${r.data.created} members${r.data.errors.length ? `, ${r.data.errors.length} skipped` : ""}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    e.target.value = "";
  };

  const openLedger = async (m) => {
    const r = await api.get(`/accounting/ledger/member/${m.member_id}`);
    setLedger(r.data);
  };

  if (!members) return <Loading />;

  return (
    <div>
      <PageHeader title="Members" subtitle="Manage your society members and their ledgers.">
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={doImport} data-testid="member-import-input" />
        <Button variant="outline" className="rounded-full gap-2" onClick={() => fileRef.current.click()} data-testid="member-import"><Upload className="w-4 h-4" /> Import Excel</Button>
        <Button className="rounded-full gap-2" onClick={() => { setForm({ ownership_type: "Owner" }); setEdit(null); setOpen(true); }} data-testid="member-add"><Plus className="w-4 h-4" /> Add Member</Button>
      </PageHeader>

      {members.length === 0 ? (
        <EmptyState icon={Users} title="No members yet" subtitle="Add members individually or import from Excel (columns: name, flat, wing, mobile, email)."
          action={<Button onClick={() => { setForm({ ownership_type: "Owner" }); setOpen(true); }} className="rounded-full gap-2"><Plus className="w-4 h-4" /> Add Member</Button>} />
      ) : (
        <div className="bg-card border rounded-xl overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Flat</TableHead><TableHead>Type</TableHead>
              <TableHead>Mobile</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.member_id} data-testid={`member-row-${m.member_id}`}>
                  <TableCell className="font-medium">{m.name} {m.is_demo && <DemoBadge />}</TableCell>
                  <TableCell>{m.wing}{m.flat}</TableCell>
                  <TableCell>{m.ownership_type}</TableCell>
                  <TableCell>{m.mobile}</TableCell>
                  <TableCell><span className="text-xs rounded-full bg-secondary/10 text-secondary px-2 py-0.5">{m.status}</span></TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openLedger(m)} data-testid={`member-ledger-${m.member_id}`}><ScrollText className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => { setForm(m); setEdit(m.member_id); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(m.member_id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="member-dialog">
          <DialogHeader><DialogTitle className="font-display">{edit ? "Edit Member" : "Add Member"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Name *" span><Input data-testid="member-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Flat *"><Input data-testid="member-flat" value={form.flat || ""} onChange={(e) => setForm({ ...form, flat: e.target.value })} /></F>
            <F label="Wing"><Input value={form.wing || ""} onChange={(e) => setForm({ ...form, wing: e.target.value })} /></F>
            <F label="Mobile"><Input value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></F>
            <F label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
            <F label="Ownership">
              <Select value={form.ownership_type || "Owner"} onValueChange={(v) => setForm({ ...form, ownership_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Owner">Owner</SelectItem><SelectItem value="Tenant">Tenant</SelectItem></SelectContent>
              </Select>
            </F>
            {!edit && <F label="Opening Balance"><Input type="number" value={form.opening_balance || ""} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} /></F>}
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="member-save">{edit ? "Update" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!ledger} onOpenChange={(o) => !o && setLedger(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle className="font-display">{ledger?.member?.name} — Ledger</SheetTitle></SheetHeader>
          {ledger && (
            <div className="mt-4">
              <div className="bg-muted rounded-lg p-3 mb-3 flex justify-between"><span className="text-sm">Closing Balance</span><span className="font-semibold">{inr(ledger.closing)}</span></div>
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Particular</TableHead><TableHead className="text-right">Dr</TableHead><TableHead className="text-right">Cr</TableHead><TableHead className="text-right">Bal</TableHead></TableRow></TableHeader>
                <TableBody>
                  {ledger.rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No transactions</TableCell></TableRow> :
                    ledger.rows.map((r, i) => (
                      <TableRow key={i}><TableCell className="text-xs">{r.date}</TableCell><TableCell className="text-xs">{r.particular}</TableCell>
                        <TableCell className="text-right text-xs">{r.debit ? inr(r.debit) : "-"}</TableCell><TableCell className="text-right text-xs">{r.credit ? inr(r.credit) : "-"}</TableCell>
                        <TableCell className="text-right text-xs font-medium">{inr(r.balance)}</TableCell></TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function F({ label, children, span }) {
  return <div className={span ? "col-span-2" : ""}><Label className="mb-1.5 block text-sm">{label}</Label>{children}</div>;
}
